import React, { useState, useEffect, useRef } from 'react';
import { getKoachProfile, updateKoachBgColor, updateKoachBgImage, updateKoachProfileImage, updateKoachNickname, updateKoachHandler, updateKoachIntroduction, generateKoachIntroduction, updateKoachAvailableLanguages, IGetKoachProfileResponse } from 'api/koach/profile.api';
import { getImageUrl } from 'helpers/Image.helper';
import { getCurrentLangName, getFlagByCountryCode, getGenderEmoji } from 'helpers/String.helper';
import { IUserKoach } from 'api/interfaces/user.interface';
import { uploadImage } from 'api/common/Data.api';
import { getLanguageCodeList } from 'data/common.data';
import { ILanguageCode } from 'api/interfaces/app.interface';

interface ProfileData {
    profileImage: string;
    profileImageCircle: string;
    nickname: string;
    handler: string;
    firstName: string;
    lastName: string;
    gender: string;
    country: string;
    phone: string;
    email: string;
    koachType: string;
    availableLanguages: string[];
    availableLanguageList: Array<{ languageCode: string; level: number }>;
    bio: string;
    specialties: string[];
    experience: string;
    education: string;
}

const KoachProfileBasic = () => {
    const [profile, setProfile] = useState<ProfileData>({
        profileImage: '/imgs/dummy/basic-img.png',
        profileImageCircle: '/imgs/dummy/basic-img.png',
        nickname: '',
        handler: '',
        firstName: '',
        lastName: '',
        gender: '',
        country: '',
        phone: '',
        email: '',
        koachType: '',
        availableLanguages: [],
        availableLanguageList: [],
        bio: '',
        specialties: [],
        experience: '',
        education: ''
    });
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [koachInfo, setKoachInfo] = useState<IUserKoach | null>(null);
    const [countryCode, setCountryCode] = useState<string>('');
    const [genderTypeCode, setGenderTypeCode] = useState<string>('');
    const [averageRating, setAverageRating] = useState<number | null>(null);
    const [bgColorHexCode, setBgColorHexCode] = useState<string | null>(null);
    const [bgImageId, setBgImageId] = useState<string | null>(null);
    const [bgImageUrl, setBgImageUrl] = useState<string | null>(null);
    const [showHeaderTooltip, setShowHeaderTooltip] = useState(false);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [showNicknameModal, setShowNicknameModal] = useState(false);
    const [editingNickname, setEditingNickname] = useState('');
    const [isUpdatingNickname, setIsUpdatingNickname] = useState(false);
    const [showHandlerModal, setShowHandlerModal] = useState(false);
    const [editingHandler, setEditingHandler] = useState('');
    const [isUpdatingHandler, setIsUpdatingHandler] = useState(false);
    const [showBioModal, setShowBioModal] = useState(false);
    const [editingBio, setEditingBio] = useState('');
    const [isUpdatingBio, setIsUpdatingBio] = useState(false);
    const [isGeneratingBio, setIsGeneratingBio] = useState(false);
    const [showLanguagesModal, setShowLanguagesModal] = useState(false);
    const [selectedLanguages, setSelectedLanguages] = useState<Array<{ languageCode: string; level: number }>>([]);
    const [isUpdatingLanguages, setIsUpdatingLanguages] = useState(false);
    const [availableLanguageList, setAvailableLanguageList] = useState<ILanguageCode[]>([]);
    const [availableLanguageCodes, setAvailableLanguageCodes] = useState<string[]>([]);
    const [isUpdatingProfileImage, setIsUpdatingProfileImage] = useState(false);
    const [isUpdatingBgImage, setIsUpdatingBgImage] = useState(false);
    const [showProfileImageTooltip, setShowProfileImageTooltip] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const profileImageInputRef = useRef<HTMLInputElement>(null);
    const profileImageCameraInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        fetchProfile();
        // 언어 목록 로드
        const languageList = getLanguageCodeList();
        if (languageList && languageList.length > 0) {
            setAvailableLanguageList(languageList);
        }
    }, []);

    const fetchProfile = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const response: IGetKoachProfileResponse = await getKoachProfile();

            if (response?.info) {
                const info = response.info as IUserKoach & { profileImageInfo?: { id: string; url: string; }; bgImageInfo?: { id: string; url: string; }; availableLanguages?: string[]; };
                setKoachInfo(info);

                console.log('KoachProfileBasic - API Response:', info);
                console.log('KoachProfileBasic - averageRating:', info.averageRating);

                // 프로필 이미지 URL 생성 (리사이즈된 URL 사용: 원형 320x320)
                let profileImageUrl = '/imgs/dummy/basic-img.png';
                let profileImageUrlForCircle = '/imgs/dummy/basic-img.png';
                if (info.profileImageInfo) {
                    const resized = getImageUrl(info.profileImageInfo, 320);
                    profileImageUrl = resized;
                    profileImageUrlForCircle = resized;
                } else if (info.profileImage) {
                    profileImageUrl = getImageUrl(info.profileImage, 320);
                    profileImageUrlForCircle = getImageUrl(info.profileImage, 320);
                }

                // 성별 텍스트 및 코드
                const genderText = info.genderType ? getCurrentLangName(info.genderType.names) : '';
                const genderCodeValue = info.genderTypeCode || '';
                setGenderTypeCode(genderCodeValue);

                // 국가 텍스트
                const countryText = info.country ? getCurrentLangName(info.country.names) : '';
                const countryCodeValue = info.country?.code || info.countryCode || '';
                setCountryCode(countryCodeValue);

                // 코치 유형 텍스트
                const koachTypeText = info.koachType ? getCurrentLangName(info.koachType.names) : '';

                // 평균 평점
                const ratingValue = (info.averageRating !== null && info.averageRating !== undefined) ? Number(info.averageRating) : null;
                console.log('KoachProfileBasic - ratingValue:', ratingValue);
                setAverageRating(ratingValue);

                // 배경 색상 코드
                console.log('KoachProfileBasic - bgColorHexCode from API:', info.bgColorHexCode);
                const bgColorValue = (info.bgColorHexCode && info.bgColorHexCode.trim()) ? info.bgColorHexCode.trim() : null;
                console.log('KoachProfileBasic - bgColorValue after processing:', bgColorValue);
                setBgColorHexCode(bgColorValue);

                // 배경 이미지 (리사이즈된 URL 사용: 1200x600)
                const bgImageIdValue = info.bgImageId || null;
                setBgImageId(bgImageIdValue);
                if (info.bgImageInfo) {
                    setBgImageUrl(getImageUrl(info.bgImageInfo, 1200));
                } else if (bgImageIdValue) {
                    // bgImageInfo가 없으면 id로 URL 생성 시도 (url/subUrl 없으면 더미 반환)
                    setBgImageUrl(getImageUrl(bgImageIdValue, 1200));
                } else {
                    setBgImageUrl(null);
                }

                // 전화번호 조합
                const phoneText = info.countryPhoneCode && info.cellphone
                    ? `+${info.countryPhoneCode} ${info.cellphone}`
                    : info.cellphone || '';

                // 가능 언어 목록
                const availableLanguages: string[] = info.availableLanguages || [];
                const availableLanguageCodesValue: string[] = (info as any).availableLanguageCodes || [];
                const availableLanguageListValue: Array<{ languageCode: string; level: number }> = (info as any).availableLanguageList || [];

                setAvailableLanguageCodes(availableLanguageCodesValue);

                setProfile({
                    profileImage: profileImageUrl,
                    profileImageCircle: profileImageUrlForCircle,
                    nickname: info.nickname || '',
                    handler: info.handler ? `@${info.handler}` : '',
                    firstName: info.firstName || '',
                    lastName: info.lastName || '',
                    gender: genderText,
                    country: countryText,
                    phone: phoneText,
                    email: '', // 이메일은 User 테이블에 있으므로 별도로 가져와야 할 수 있음
                    koachType: koachTypeText,
                    availableLanguages: availableLanguages,
                    availableLanguageList: availableLanguageListValue,
                    bio: info.introduction || '',
                    specialties: [], // 전문 분야는 별도 필드가 필요할 수 있음
                    experience: '', // 경력은 별도 필드가 필요할 수 있음
                    education: '' // 학력은 별도 필드가 필요할 수 있음
                });
            }
        } catch (err: any) {
            console.error('프로필 정보를 불러오는데 실패했습니다:', err);
            setError(err.message || '프로필 정보를 불러오는데 실패했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (field: string, value: string) => {
        setProfile(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = () => {
        // 저장 로직
        setIsEditing(false);
        alert('프로필이 저장되었습니다.');
    };

    const handleNicknameEdit = () => {
        setEditingNickname(profile.nickname);
        setShowNicknameModal(true);
    };

    const handleNicknameSave = async () => {
        // 이미 업데이트 중이면 중복 요청 방지
        if (isUpdatingNickname) {
            return;
        }

        try {
            setIsUpdatingNickname(true);
            await updateKoachNickname(editingNickname);
            setProfile(prev => ({ ...prev, nickname: editingNickname }));
            setShowNicknameModal(false);
            setEditingNickname('');
        } catch (err: any) {
            console.error('닉네임 저장 실패:', err);

            // 에러 메시지 추출
            let errorMessage = '닉네임 저장에 실패했습니다.';
            if (err?.message) {
                errorMessage = `닉네임 저장에 실패했습니다.\n\n사유: ${err.message}`;
            } else if (err?.response?.data?.message) {
                errorMessage = `닉네임 저장에 실패했습니다.\n\n사유: ${err.response.data.message}`;
            } else if (err?.response?.data?.error) {
                errorMessage = `닉네임 저장에 실패했습니다.\n\n사유: ${err.response.data.error}`;
            } else if (typeof err === 'string') {
                errorMessage = `닉네임 저장에 실패했습니다.\n\n사유: ${err}`;
            }

            alert(errorMessage);
        } finally {
            setIsUpdatingNickname(false);
        }
    };

    const handleNicknameCancel = () => {
        setShowNicknameModal(false);
        setEditingNickname('');
    };

    const handleHandlerEdit = () => {
        // @ 제거하여 편집
        const handlerWithoutAt = profile.handler.startsWith('@') ? profile.handler.slice(1) : profile.handler;
        setEditingHandler(handlerWithoutAt);
        setShowHandlerModal(true);
    };

    const handleHandlerSave = async () => {
        if (isUpdatingHandler) {
            return;
        }

        try {
            setIsUpdatingHandler(true);
            await updateKoachHandler(editingHandler);
            setProfile(prev => ({ ...prev, handler: `@${editingHandler}` }));
            setShowHandlerModal(false);
            setEditingHandler('');
        } catch (err: any) {
            console.error('핸들러 저장 실패:', err);

            let errorMessage = '핸들러 저장에 실패했습니다.';
            
            // 핸들러 중복 에러 체크
            if (err?.response?.data?.error === 'HANDLER_ALREADY_IN_USE' || 
                err?.response?.data?.message?.includes('HANDLER_ALREADY_IN_USE') ||
                err?.message?.includes('HANDLER_ALREADY_IN_USE')) {
                errorMessage = '이미 사용 중인 핸들러입니다.\n다른 핸들러를 입력해주세요.';
            } else if (err?.message) {
                errorMessage = `핸들러 저장에 실패했습니다.\n\n사유: ${err.message}`;
            } else if (err?.response?.data?.message) {
                errorMessage = `핸들러 저장에 실패했습니다.\n\n사유: ${err.response.data.message}`;
            } else if (err?.response?.data?.error) {
                errorMessage = `핸들러 저장에 실패했습니다.\n\n사유: ${err.response.data.error}`;
            } else if (typeof err === 'string') {
                errorMessage = `핸들러 저장에 실패했습니다.\n\n사유: ${err}`;
            }

            alert(errorMessage);
        } finally {
            setIsUpdatingHandler(false);
        }
    };

    const handleHandlerCancel = () => {
        setShowHandlerModal(false);
        setEditingHandler('');
    };

    const handleBioEdit = () => {
        setEditingBio(profile.bio);
        setShowBioModal(true);
    };

    const handleBioSave = async () => {
        if (isUpdatingBio) {
            return;
        }

        try {
            setIsUpdatingBio(true);
            await updateKoachIntroduction(editingBio);
            setProfile(prev => ({ ...prev, bio: editingBio }));
            setShowBioModal(false);
            setEditingBio('');
        } catch (err: any) {
            console.error('자기소개 저장 실패:', err);

            let errorMessage = '자기소개 저장에 실패했습니다.';
            if (err?.message) {
                errorMessage = `자기소개 저장에 실패했습니다.\n\n사유: ${err.message}`;
            } else if (err?.response?.data?.message) {
                errorMessage = `자기소개 저장에 실패했습니다.\n\n사유: ${err.response.data.message}`;
            } else if (err?.response?.data?.error) {
                errorMessage = `자기소개 저장에 실패했습니다.\n\n사유: ${err.response.data.error}`;
            } else if (typeof err === 'string') {
                errorMessage = `자기소개 저장에 실패했습니다.\n\n사유: ${err}`;
            }

            alert(errorMessage);
        } finally {
            setIsUpdatingBio(false);
        }
    };

    const handleBioCancel = () => {
        setShowBioModal(false);
        setEditingBio('');
    };

    const handleLanguagesEdit = () => {
        // 기존 선택된 언어와 레벨 정보를 함께 로드
        const currentLanguages = profile.availableLanguageList.map(lang => ({
            languageCode: lang.languageCode,
            level: lang.level
        }));
        setSelectedLanguages(currentLanguages);
        setShowLanguagesModal(true);
    };

    const handleLanguagesSave = async () => {
        if (isUpdatingLanguages) {
            return;
        }

        try {
            setIsUpdatingLanguages(true);
            const languageList: Array<{ languageCode: string; level: number }> = selectedLanguages;
            await updateKoachAvailableLanguages(languageList);
            
            // 프로필 다시 불러오기
            await fetchProfile();
            
            setShowLanguagesModal(false);
            setSelectedLanguages([]);
        } catch (err: any) {
            console.error('구사 가능한 언어 저장 실패:', err);

            let errorMessage = '구사 가능한 언어 저장에 실패했습니다.';
            if (err?.message) {
                errorMessage = `구사 가능한 언어 저장에 실패했습니다.\n\n사유: ${err.message}`;
            } else if (err?.response?.data?.message) {
                errorMessage = `구사 가능한 언어 저장에 실패했습니다.\n\n사유: ${err.response.data.message}`;
            } else if (err?.response?.data?.error) {
                errorMessage = `구사 가능한 언어 저장에 실패했습니다.\n\n사유: ${err.response.data.error}`;
            } else if (typeof err === 'string') {
                errorMessage = `구사 가능한 언어 저장에 실패했습니다.\n\n사유: ${err}`;
            }

            alert(errorMessage);
        } finally {
            setIsUpdatingLanguages(false);
        }
    };

    const handleLanguagesCancel = () => {
        setShowLanguagesModal(false);
        setSelectedLanguages([]);
    };

    const handleLanguageToggle = (languageCode: string) => {
        const existingIndex = selectedLanguages.findIndex(lang => lang.languageCode === languageCode);
        if (existingIndex >= 0) {
            // 이미 선택된 경우 제거
            setSelectedLanguages(selectedLanguages.filter(lang => lang.languageCode !== languageCode));
        } else {
            // 새로 선택하는 경우 기본 레벨(1)로 추가
            setSelectedLanguages([...selectedLanguages, { languageCode, level: 1 }]);
        }
    };

    const handleLanguageLevelChange = (languageCode: string, level: number) => {
        setSelectedLanguages(selectedLanguages.map(lang => 
            lang.languageCode === languageCode ? { ...lang, level } : lang
        ));
    };

    const handleBioGenerate = async () => {
        if (isGeneratingBio) {
            return;
        }

        try {
            setIsGeneratingBio(true);
            const result = await generateKoachIntroduction(editingBio);
            if (result?.introduction) {
                // 300자 제한
                const generatedText = result.introduction.length > 300 
                    ? result.introduction.substring(0, 300) 
                    : result.introduction;
                setEditingBio(generatedText);
            }
        } catch (err: any) {
            console.error('자기소개 생성 실패:', err);

            let errorMessage = '자기소개 생성에 실패했습니다.';
            if (err?.message) {
                errorMessage = `자기소개 생성에 실패했습니다.\n\n사유: ${err.message}`;
            } else if (err?.response?.data?.message) {
                errorMessage = `자기소개 생성에 실패했습니다.\n\n사유: ${err.response.data.message}`;
            } else if (err?.response?.data?.error) {
                errorMessage = `자기소개 생성에 실패했습니다.\n\n사유: ${err.response.data.error}`;
            } else if (typeof err === 'string') {
                errorMessage = `자기소개 생성에 실패했습니다.\n\n사유: ${err}`;
            }

            alert(errorMessage);
        } finally {
            setIsGeneratingBio(false);
        }
    };

    const handleColorChange = () => {
        setShowHeaderTooltip(false);
        setShowColorPicker(true);
    };

    const handleColorSelect = async (colorHex: string) => {
        // # 제거 (서버에는 # 없이 저장)
        const hexWithoutHash = colorHex.replace('#', '');

        // 즉시 UI에 적용 (배경 이미지 제거)
        setBgColorHexCode(hexWithoutHash);
        setBgImageId(null);
        setBgImageUrl(null);
        setShowColorPicker(false);

        try {
            // 서버에 저장 (배경 색상 저장 및 배경 이미지 삭제)
            await updateKoachBgColor(hexWithoutHash);
        } catch (err: any) {
            console.error('배경 색상 저장 실패:', err);

            // 에러 메시지 추출
            let errorMessage = '배경 색상 저장에 실패했습니다.';
            if (err?.message) {
                errorMessage = `배경 색상 저장에 실패했습니다.\n\n사유: ${err.message}`;
            } else if (err?.response?.data?.message) {
                errorMessage = `배경 색상 저장에 실패했습니다.\n\n사유: ${err.response.data.message}`;
            } else if (err?.response?.data?.error) {
                errorMessage = `배경 색상 저장에 실패했습니다.\n\n사유: ${err.response.data.error}`;
            } else if (typeof err === 'string') {
                errorMessage = `배경 색상 저장에 실패했습니다.\n\n사유: ${err}`;
            }

            alert(errorMessage);
            // 실패 시 이전 값으로 복원
            fetchProfile();
        }
    };

    const handleImageChange = () => {
        setShowHeaderTooltip(false);
        // 파일 선택 대화상자 열기
        fileInputRef.current?.click();
    };

    const handleImageFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        if (isUpdatingBgImage) {
            return;
        }

        try {
            setIsUpdatingBgImage(true);

            // 이미지 업로드
            const uploadResult = await uploadImage(file, true);

            // bgImageId 업데이트 및 배경 이미지 정보 받기
            const updateResult = await updateKoachBgImage(uploadResult.id);
            console.log('배경 이미지 업데이트 응답:', updateResult);

            // 상태 업데이트 (리사이즈 URL: 1200)
            if (updateResult?.bgImageInfo) {
                setBgImageId(updateResult.bgImageInfo.id);
                setBgImageUrl(getImageUrl(updateResult.bgImageInfo, 1200));
            } else {
                console.warn('bgImageInfo가 응답에 없습니다. 업로드된 이미지 ID로 URL 생성');
                setBgImageId(uploadResult.id);
                setBgImageUrl(getImageUrl(uploadResult.id, 1200));
            }
        } catch (err: any) {
            console.error('배경 이미지 업로드 실패:', err);

            // 에러 메시지 추출
            let errorMessage = '배경 이미지 업로드에 실패했습니다.';
            if (err?.message) {
                errorMessage = `배경 이미지 업로드에 실패했습니다.\n\n사유: ${err.message}`;
            } else if (err?.response?.data?.message) {
                errorMessage = `배경 이미지 업로드에 실패했습니다.\n\n사유: ${err.response.data.message}`;
            } else if (err?.response?.data?.error) {
                errorMessage = `배경 이미지 업로드에 실패했습니다.\n\n사유: ${err.response.data.error}`;
            } else if (typeof err === 'string') {
                errorMessage = `배경 이미지 업로드에 실패했습니다.\n\n사유: ${err}`;
            }

            alert(errorMessage);
        } finally {
            setIsUpdatingBgImage(false);
            // 파일 input 초기화
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleProfileImageClick = () => {
        setShowProfileImageTooltip(!showProfileImageTooltip);
    };

    const handleProfileImageFromGallery = () => {
        setShowProfileImageTooltip(false);
        profileImageInputRef.current?.click();
    };

    const handleProfileImageFromCamera = () => {
        setShowProfileImageTooltip(false);
        profileImageCameraInputRef.current?.click();
    };

    const handleProfileImageFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        if (isUpdatingProfileImage) {
            return;
        }

        try {
            setIsUpdatingProfileImage(true);

            // 이미지 업로드
            const uploadResult = await uploadImage(file, true);

            // 프로필 이미지 업데이트
            const updateResult = await updateKoachProfileImage(uploadResult.id);
            console.log('프로필 이미지 업데이트 응답:', updateResult);

            // 상태 업데이트 (리사이즈 URL: 320x320)
            if (updateResult?.profileImageInfo) {
                const newUrl = getImageUrl(updateResult.profileImageInfo, 320);
                setProfile(prev => ({
                    ...prev,
                    profileImage: newUrl,
                    profileImageCircle: newUrl
                }));
            } else {
                console.warn('profileImageInfo가 응답에 없습니다. 업로드된 이미지로 URL 생성');
                const newUrl = getImageUrl(uploadResult, 320);
                setProfile(prev => ({
                    ...prev,
                    profileImage: newUrl,
                    profileImageCircle: newUrl
                }));
            }
        } catch (err: any) {
            console.error('프로필 이미지 업로드 실패:', err);

            // 에러 메시지 추출
            let errorMessage = '프로필 이미지 업로드에 실패했습니다.';
            if (err?.message) {
                errorMessage = `프로필 이미지 업로드에 실패했습니다.\n\n사유: ${err.message}`;
            } else if (err?.response?.data?.message) {
                errorMessage = `프로필 이미지 업로드에 실패했습니다.\n\n사유: ${err.response.data.message}`;
            } else if (err?.response?.data?.error) {
                errorMessage = `프로필 이미지 업로드에 실패했습니다.\n\n사유: ${err.response.data.error}`;
            } else if (typeof err === 'string') {
                errorMessage = `프로필 이미지 업로드에 실패했습니다.\n\n사유: ${err}`;
            }

            alert(errorMessage);
        } finally {
            setIsUpdatingProfileImage(false);
            // 파일 input 초기화
            if (profileImageInputRef.current) {
                profileImageInputRef.current.value = '';
            }
        }
    };

    // 외부 클릭 시 툴팁 및 컬러피커 닫기
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (showHeaderTooltip && !target.closest('.header-edit-tooltip')) {
                setShowHeaderTooltip(false);
            }
            if (showColorPicker && !target.closest('.color-picker-container')) {
                setShowColorPicker(false);
            }
            if (showProfileImageTooltip && !target.closest('.profile-image-edit-tooltip')) {
                setShowProfileImageTooltip(false);
            }
        };

        if (showHeaderTooltip || showColorPicker || showProfileImageTooltip) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showHeaderTooltip, showColorPicker, showProfileImageTooltip]);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 pt-20 pb-24 px-4">
                <div className="max-w-4xl mx-auto">
                    {/* 프로필 카드 스켈레톤 */}
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden relative animate-pulse">
                        {/* 헤더 스켈레톤 */}
                        <div className="h-64 md:h-80 bg-gray-200"></div>
                        
                        {/* 평점 영역 스켈레톤 */}
                        <div className="px-6 pt-16 md:pt-20 pb-4 border-b border-gray-200">
                            <div className="h-6 bg-gray-200 rounded w-32"></div>
                        </div>
                        
                        {/* 프로필 콘텐츠 영역 스켈레톤 */}
                        <div className="p-6 space-y-6">
                            {/* 기본 정보 스켈레톤 */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                                    <div className="h-5 bg-gray-200 rounded w-24"></div>
                                </div>
                                <div className="space-y-2">
                                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                                    <div className="h-5 bg-gray-200 rounded w-32"></div>
                                </div>
                            </div>
                            
                            {/* Koach 소개 스켈레톤 */}
                            <div className="space-y-2">
                                <div className="h-4 bg-gray-200 rounded w-20"></div>
                                <div className="space-y-2">
                                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                                    <div className="h-4 bg-gray-200 rounded w-4/6"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 pt-20 pb-24 px-4">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <div className="flex flex-col justify-center items-center py-12">
                            <div className="text-red-500 mb-4">{error}</div>
                            <button
                                onClick={fetchProfile}
                                className="px-4 py-2 bg-[#CF3688] text-white rounded-lg hover:bg-[#B52E74] transition-colors"
                            >
                                다시 시도
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="min-h-screen bg-gray-50 pt-20 pb-24 px-4">
                <div className="max-w-4xl mx-auto">
                    {/* 프로필 카드 */}
                    <div className="bg-white rounded-lg shadow-sm overflow-visible relative">
                        {/* 프로필 헤더 (배경 이미지 우선, 없으면 컬러) */}
                        <div
                            className="relative h-64 md:h-80 overflow-visible rounded-t-lg"
                        >
                            {/* 배경 (rounded-t-lg를 위한 마스킹) */}
                            <div className="absolute inset-0 overflow-hidden rounded-t-lg">
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        ...(bgImageUrl
                                            ? { backgroundImage: `url(${bgImageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                                            : bgColorHexCode
                                                ? { backgroundColor: `#${bgColorHexCode}` }
                                                : { background: 'linear-gradient(to right, #CF3688, #B52E74)' }
                                        )
                                    }}
                                />
                                {/* 배경 이미지 오버레이 (가독성을 위한 어두운 그라데이션) */}
                                {bgImageUrl && (
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent" />
                                )}
                            </div>

                            {/* 숨겨진 파일 input */}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleImageFileSelect}
                                className="hidden"
                            />
                            {/* 편집 아이콘 (우측 상단) */}
                            <div className="header-edit-tooltip absolute top-4 right-4 z-20">
                                <button
                                    onClick={() => !isUpdatingBgImage && setShowHeaderTooltip(!showHeaderTooltip)}
                                    disabled={isUpdatingBgImage}
                                    className="w-8 h-8 bg-black/40 hover:bg-black/50 backdrop-blur-sm text-white rounded-full flex items-center justify-center shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    aria-label="편집"
                                >
                                    {isUpdatingBgImage ? (
                                        <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    ) : (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                    )}
                                </button>

                                {/* 툴팁 메뉴 */}
                                {showHeaderTooltip && (
                                    <div className="absolute top-12 right-0 mt-2 w-40 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50">
                                        <button
                                            onClick={handleColorChange}
                                            className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                                            </svg>
                                            <span>색깔 변경</span>
                                        </button>
                                        <button
                                            onClick={handleImageChange}
                                            className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2 border-t border-gray-100"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            <span>이미지 변경</span>
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* 하단 그라데이션 배경 및 정보 영역 */}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent pt-16 pb-6 px-6">
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                    <div className="flex flex-col gap-1">
                                        {/* 닉네임 */}
                                        <div className="flex items-center gap-3">
                                            <div className="relative inline-block">
                                                <h2 className="text-2xl md:text-3xl font-bold text-white">
                                                    {profile.nickname || '-'}
                                                </h2>
                                                <button
                                                    onClick={handleNicknameEdit}
                                                    className="absolute -top-3 -right-5 w-6 h-6 bg-black/40 hover:bg-black/50 backdrop-blur-sm text-white rounded-full flex items-center justify-center shadow-lg transition-all"
                                                    title="닉네임 수정"
                                                >
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>

                                        {/* 코치 유형, 성별, 국적 깃발 (한 줄) */}
                                        <div className="flex items-center gap-2 text-sm md:text-base text-white/95 font-medium">
                                            {profile.koachType && (
                                                <span>{profile.koachType}</span>
                                            )}
                                            {profile.koachType && genderTypeCode && (
                                                <span className="text-white/60">•</span>
                                            )}
                                            {genderTypeCode && (
                                                <span className="text-base md:text-lg">
                                                    {getGenderEmoji(genderTypeCode)}
                                                </span>
                                            )}
                                            {countryCode && (
                                                <>
                                                    {(profile.koachType || genderTypeCode) && (
                                                        <span className="text-white/60">•</span>
                                                    )}
                                                    <span className="text-base md:text-lg">
                                                        {getFlagByCountryCode(countryCode)}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 원형 프로필 이미지 (헤더 하단 경계에 반쯤 걸치게 위치) */}
                            <div className="absolute bottom-0 right-6 z-10" style={{ transform: 'translateY(50%)' }}>
                                <div className="profile-image-edit-tooltip relative">
                                    {/* 숨겨진 프로필 이미지 파일 input (사진첩용) */}
                                    <input
                                        ref={profileImageInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleProfileImageFileSelect}
                                        className="hidden"
                                    />
                                    {/* 숨겨진 프로필 이미지 파일 input (카메라용) */}
                                    <input
                                        ref={profileImageCameraInputRef}
                                        type="file"
                                        accept="image/*"
                                        capture="user"
                                        onChange={handleProfileImageFileSelect}
                                        className="hidden"
                                    />
                                    <img
                                        src={profile.profileImageCircle}
                                        alt="프로필"
                                        className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-4 border-white shadow-lg"
                                    />
                                    {/* 프로필 이미지 편집 버튼 (우측 상단) */}
                                    <button
                                        onClick={handleProfileImageClick}
                                        disabled={isUpdatingProfileImage}
                                        className="absolute top-0 right-0 w-8 h-8 bg-black/40 hover:bg-black/50 backdrop-blur-sm text-white rounded-full flex items-center justify-center shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="프로필 이미지 변경"
                                    >
                                        {isUpdatingProfileImage ? (
                                            <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        ) : (
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        )}
                                    </button>

                                    {/* 프로필 이미지 편집 툴팁 */}
                                    {showProfileImageTooltip && (
                                        <div className="absolute top-10 right-0 mt-1 w-36 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50">
                                            <button
                                                onClick={handleProfileImageFromGallery}
                                                className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                                <span>사진첩</span>
                                            </button>
                                            <button
                                                onClick={handleProfileImageFromCamera}
                                                className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2 border-t border-gray-100"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                                <span>카메라</span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* 컬러피커 (헤더 영역 밖에 배치) */}
                        {showColorPicker && (
                            <div className="color-picker-container absolute top-16 right-4 z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-4">
                                <div className="mb-3">
                                    <h3 className="text-sm font-semibold text-gray-700 mb-2">배경 색상 선택</h3>
                                    <div className="grid grid-cols-8 gap-2">
                                        {/* 인기 색상 팔레트 */}
                                        {[
                                            'FF6B9D', 'CF3688', 'B52E74', '9B1B5A',
                                            '4ECDC4', '45B7D1', '96CEB4', 'FFEAA7',
                                            'FFD93D', 'FF6B6B', 'FF8E53', 'FFA07A',
                                            '98D8C8', 'F7DC6F', 'BB8FCE', '85C1E2',
                                            'F8B739', 'E74C3C', 'C0392B', '8E44AD',
                                            '3498DB', '1ABC9C', '16A085', '27AE60',
                                            '2ECC71', 'F39C12', 'E67E22', 'D35400',
                                            '34495E', '2C3E50', '95A5A6', '7F8C8D',
                                            'ECF0F1', 'BDC3C7', '95A5A6', '7F8C8D',
                                            '000000', 'FFFFFF', 'E0E0E0', 'BDBDBD'
                                        ].map((color) => (
                                            <button
                                                key={color}
                                                onClick={() => handleColorSelect(`#${color}`)}
                                                className={`w-8 h-8 rounded border-2 transition-all shadow-sm hover:scale-110 ${bgColorHexCode === color
                                                        ? 'border-gray-800 ring-2 ring-gray-400 ring-offset-1'
                                                        : 'border-gray-300 hover:border-gray-400'
                                                    }`}
                                                style={{ backgroundColor: `#${color}` }}
                                                title={`#${color}`}
                                            />
                                        ))}
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowColorPicker(false)}
                                    className="mt-3 w-full px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded transition-colors"
                                >
                                    닫기
                                </button>
                            </div>
                        )}

                        {/* 평균 별점 (헤더 바로 아래) */}
                        <div className="px-6 pt-16 md:pt-20 pb-4 border-b border-gray-200">
                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-1">
                                    {averageRating !== null && averageRating !== undefined ? (
                                        Array.from({ length: 5 }, (_, i) => {
                                            // 10점 만점을 5점 만점으로 변환하고 0.5 단위로 반올림
                                            const rating5 = Math.round((averageRating / 2) * 2) / 2;
                                            const filled = i < rating5;
                                            return (
                                                <span
                                                    key={i}
                                                    className={`text-xl ${filled ? 'text-yellow-400' : 'text-gray-300'}`}
                                                >
                                                    ★
                                                </span>
                                            );
                                        })
                                    ) : (
                                        <span className="text-xl text-gray-300">★</span>
                                    )}
                                </div>
                                <span className="text-lg font-medium text-gray-900">
                                    {averageRating !== null && averageRating !== undefined
                                        ? Math.round((averageRating / 2) * 2) / 2
                                        : '아직 평점이 없습니다'}
                                </span>
                            </div>
                        </div>

                        {/* 프로필 콘텐츠 영역 */}
                        <div className="p-6 space-y-6">
                            {/* 기본 정보 */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* 핸들러 */}
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            핸들러
                                        </label>
                                        <button
                                            onClick={handleHandlerEdit}
                                            className="w-5 h-5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full flex items-center justify-center transition-colors"
                                            title="핸들러 수정"
                                        >
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        </button>
                                    </div>
                                    <p className="text-gray-900">{profile.handler || '-'}</p>
                                </div>

                                {/* 구사 가능한 언어 */}
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            구사 가능한 언어
                                        </label>
                                        <button
                                            onClick={handleLanguagesEdit}
                                            className="w-5 h-5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full flex items-center justify-center transition-colors"
                                            title="구사 가능한 언어 수정"
                                        >
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        </button>
                                    </div>
                                    <div className="space-y-2">
                                        {profile.availableLanguageList.length > 0 ? (
                                            <ul className="space-y-2">
                                                {profile.availableLanguageList.map((lang, index) => {
                                                    const languageName = profile.availableLanguages[index] || '';
                                                    const levelText = lang.level === 1 ? '기본 대화 가능' : 
                                                                     lang.level === 2 ? '코칭 피드백 가능' : 
                                                                     lang.level === 3 ? '라이브 코칭 가능' : '';
                                                    return (
                                                        <li key={lang.languageCode} className="flex items-center justify-between">
                                                            <span className="text-gray-900 flex items-center">
                                                                <span className="mr-2">•</span>
                                                                {languageName}
                                                            </span>
                                                            <span className="text-sm text-gray-500 ml-2">{levelText}</span>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        ) : (
                                            <p className="text-gray-900">-</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Koach 소개 */}
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Koach 소개
                                    </label>
                                    <button
                                        onClick={handleBioEdit}
                                        className="w-5 h-5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full flex items-center justify-center transition-colors"
                                        title="자기소개 수정"
                                    >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                    </button>
                                </div>
                                <p className="text-gray-900 whitespace-pre-line">{profile.bio || '-'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* 닉네임 편집 팝업 */}
            {showNicknameModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={handleNicknameCancel}>
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">닉네임 수정</h3>
                        <input
                            type="text"
                            value={editingNickname}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value.length <= 8) {
                                    setEditingNickname(value);
                                }
                            }}
                            maxLength={8}
                            disabled={isUpdatingNickname}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#CF3688] focus:border-transparent mb-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            placeholder="닉네임을 입력하세요 (최대 8글자)"
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !isUpdatingNickname) {
                                    handleNicknameSave();
                                } else if (e.key === 'Escape' && !isUpdatingNickname) {
                                    handleNicknameCancel();
                                }
                            }}
                        />
                        <p className="text-sm text-gray-500 mb-4">
                            {editingNickname.length}/8 글자
                        </p>
                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={handleNicknameCancel}
                                disabled={isUpdatingNickname}
                                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                취소
                            </button>
                            <button
                                onClick={handleNicknameSave}
                                disabled={isUpdatingNickname}
                                className="px-4 py-2 bg-[#CF3688] text-white rounded-lg hover:bg-[#B52E74] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {isUpdatingNickname ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        저장 중...
                                    </>
                                ) : (
                                    '저장'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 핸들러 편집 팝업 */}
            {showHandlerModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={handleHandlerCancel}>
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">핸들러 수정</h3>
                        <input
                            type="text"
                            value={editingHandler}
                            onChange={(e) => {
                                const value = e.target.value.replace('@', '');
                                if (value.length <= 10) {
                                    setEditingHandler(value);
                                }
                            }}
                            maxLength={10}
                            disabled={isUpdatingHandler}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#CF3688] focus:border-transparent mb-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            placeholder="핸들러를 입력하세요 (@ 제외, 최대 10글자)"
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !isUpdatingHandler) {
                                    handleHandlerSave();
                                } else if (e.key === 'Escape' && !isUpdatingHandler) {
                                    handleHandlerCancel();
                                }
                            }}
                        />
                        <p className="text-sm text-gray-500 mb-4">
                            {editingHandler.length}/10 글자
                        </p>
                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={handleHandlerCancel}
                                disabled={isUpdatingHandler}
                                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                취소
                            </button>
                            <button
                                onClick={handleHandlerSave}
                                disabled={isUpdatingHandler}
                                className="px-4 py-2 bg-[#CF3688] text-white rounded-lg hover:bg-[#B52E74] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {isUpdatingHandler ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        저장 중...
                                    </>
                                ) : (
                                    '저장'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 자기소개 편집 팝업 */}
            {showBioModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={handleBioCancel}>
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">자기소개 수정</h3>
                        <textarea
                            value={editingBio}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value.length <= 300) {
                                    setEditingBio(value);
                                }
                            }}
                            maxLength={300}
                            disabled={isUpdatingBio || isGeneratingBio}
                            rows={6}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#CF3688] focus:border-transparent mb-2 disabled:opacity-50 disabled:cursor-not-allowed resize-none"
                            placeholder="자기소개를 입력하세요 (최대 300글자)"
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Escape' && !isUpdatingBio && !isGeneratingBio) {
                                    handleBioCancel();
                                }
                            }}
                        />
                        <div className="flex items-center justify-between mb-4">
                            <p className="text-sm text-gray-500">
                                {editingBio.length}/300 글자
                            </p>
                            <button
                                onClick={handleBioGenerate}
                                disabled={isGeneratingBio || isUpdatingBio}
                                className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {isGeneratingBio ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        생성 중...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                        AI 생성
                                    </>
                                )}
                            </button>
                        </div>
                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={handleBioCancel}
                                disabled={isUpdatingBio || isGeneratingBio}
                                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                취소
                            </button>
                            <button
                                onClick={handleBioSave}
                                disabled={isUpdatingBio || isGeneratingBio}
                                className="px-4 py-2 bg-[#CF3688] text-white rounded-lg hover:bg-[#B52E74] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {isUpdatingBio ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        저장 중...
                                    </>
                                ) : (
                                    '저장'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 구사 가능한 언어 편집 팝업 */}
            {showLanguagesModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={handleLanguagesCancel}>
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                        {/* 헤더 영역 */}
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">구사 가능한 언어 수정</h3>
                        </div>
                        
                        {/* 스크롤 가능한 본문 영역 */}
                        <div className="flex-1 overflow-y-auto px-6 py-4">
                            <div className="space-y-3">
                                {availableLanguageList.map((language) => {
                                    const isSelected = selectedLanguages.some(lang => lang.languageCode === language.code);
                                    const selectedLang = selectedLanguages.find(lang => lang.languageCode === language.code);
                                    const currentLevel = selectedLang?.level || 1;
                                    
                                    return (
                                        <div key={language.code} className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => handleLanguageToggle(language.code)}
                                                    disabled={isUpdatingLanguages}
                                                    className="h-4 w-4 text-[#CF3688] focus:ring-[#CF3688] rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                                />
                                                <span className="flex-1 text-gray-700 font-medium">{getCurrentLangName(language.names)}</span>
                                                {isSelected && (
                                                    <select
                                                        value={currentLevel}
                                                        onChange={(e) => handleLanguageLevelChange(language.code, parseInt(e.target.value))}
                                                        disabled={isUpdatingLanguages}
                                                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#CF3688] disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        <option value={1}>기본 대화 가능</option>
                                                        <option value={2}>코칭 피드백 가능</option>
                                                        <option value={3}>라이브 코칭 가능</option>
                                                    </select>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        
                        {/* 푸터 영역 */}
                        <div className="px-6 py-4 border-t border-gray-200">
                            <div className="flex gap-2">
                                <button
                                    onClick={handleLanguagesCancel}
                                    disabled={isUpdatingLanguages}
                                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    취소
                                </button>
                                <button
                                    onClick={handleLanguagesSave}
                                    disabled={isUpdatingLanguages}
                                    className="flex-1 px-4 py-2 bg-[#CF3688] text-white rounded-lg hover:bg-[#B52E74] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isUpdatingLanguages ? '저장 중...' : '저장'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default KoachProfileBasic;

